import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Shield, Award, ThumbsUp } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-[#003366] text-white relative overflow-hidden">
      {/* Blueprint pattern */}
      <div className="absolute inset-0 opacity-[0.02] pointer-events-none" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h100v100H0z' fill='none' stroke='%23ffffff' stroke-width='0.5'/%3E%3Cpath d='M20 0v100M40 0v100M60 0v100M80 0v100M0 20h100M0 40h100M0 60h100M0 80h100' stroke='%23ffffff' stroke-width='0.25'/%3E%3C/svg%3E")`,
        backgroundSize: '100px 100px'
      }}></div>

      {/* Gold Top Bar */}
      <div className="bg-[#CFB973] py-4 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#003366] font-bold text-lg text-center md:text-left">
              Ready to understand your home? Book your assessment today.
            </p>
            <Link to="/contact" className="bg-[#003366] hover:bg-[#003366]/90 text-white px-8 py-3 rounded-lg font-bold transition-colors shadow-lg">
              Get Started
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_smarthouse-staging/artifacts/kmsojmc6_Transparent%20Png-03.png" 
                alt="Valenrock Home Services" 
                className="h-24 w-auto"
              />
            </div>
            <p className="text-white/70 mb-6 leading-relaxed">
              One trusted partner — from assessment to renovation. We help you understand, maintain, and improve your home.
            </p>
            
            {/* Trust Badges Mini */}
            <div className="flex gap-4 flex-wrap mb-4">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center mb-1">
                  <Shield className="w-6 h-6 text-[#003366]" />
                </div>
                <span className="text-xs text-white/60">Licensed</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center mb-1">
                  <ThumbsUp className="w-6 h-6 text-[#003366]" />
                </div>
                <span className="text-xs text-white/60">Guaranteed</span>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-[#CFB973] flex items-center justify-center mb-1">
                  <Award className="w-6 h-6 text-[#003366]" />
                </div>
                <span className="text-xs text-white/60">10+ Years</span>
              </div>
            </div>
            {/* EPA Certification */}
            <div className="pt-4 border-t border-white/10">
              <div className="inline-block rounded-lg border-2 border-white/80 p-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_smarthouse-staging/artifacts/inesgu3y_IMG_7280.png" 
                  alt="EPA Lead-Safe Certified" 
                  className="h-20 w-auto object-contain rounded"
                />
              </div>
              <p className="text-white/80 text-xs mt-2">EPA Lead-Safe Certified Firm</p>
              <p className="text-white/60 text-xs">NAT-F326598-1</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-[#CFB973]">Services</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/assessment" className="text-white/70 hover:text-[#CFB973] transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#CFB973] rounded-full"></span>
                  Home Assessment
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-white/70 hover:text-[#CFB973] transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#CFB973] rounded-full"></span>
                  Maintenance & Care
                </Link>
              </li>
              <li>
                <Link to="/remodeling" className="text-white/70 hover:text-[#CFB973] transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#CFB973] rounded-full"></span>
                  Remodeling & Construction
                </Link>
              </li>
              <li>
                <Link to="/how-it-works" className="text-white/70 hover:text-[#CFB973] transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#CFB973] rounded-full"></span>
                  Our Process
                </Link>
              </li>
              <li>
                <Link to="/why-valenrock" className="text-white/70 hover:text-[#CFB973] transition-colors flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-[#CFB973] rounded-full"></span>
                  About Valenrock
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-[#CFB973]">Contact Us</h4>
            <ul className="space-y-4">
              <li>
                <a href="tel:5513799774" className="flex items-center gap-3 text-white/70 hover:text-[#CFB973] transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-[#CFB973] flex items-center justify-center transition-colors">
                    <Phone size={18} className="text-[#CFB973] group-hover:text-[#003366]" />
                  </div>
                  <span className="font-medium">(551) 379-9774</span>
                </a>
              </li>
              <li>
                <a href="mailto:contact@valenrockhome.services" className="flex items-center gap-3 text-white/70 hover:text-[#CFB973] transition-colors group">
                  <div className="w-10 h-10 rounded-full bg-white/10 group-hover:bg-[#CFB973] flex items-center justify-center transition-colors">
                    <Mail size={18} className="text-[#CFB973] group-hover:text-[#003366]" />
                  </div>
                  <span className="font-medium text-sm whitespace-nowrap">contact@valenrockhome.services</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-white/70">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MapPin size={18} className="text-[#CFB973]" />
                  </div>
                  <span className="text-sm leading-relaxed">Northern & Central NJ</span>
                </div>
              </li>
            </ul>
          </div>

          {/* Service Area */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-[#CFB973]">Proudly Serving Northern NJ & Middlesex County</h4>
            <p className="text-white/70 text-sm mb-4 leading-relaxed">
              Whole-Home Assessments & Care Plans available in:
            </p>
            <div className="grid grid-cols-2 gap-2 text-sm text-white/60">
              <span>• Bergen County</span>
              <span>• Essex County</span>
              <span>• Hudson County</span>
              <span>• Morris County</span>
              <span>• Passaic County</span>
              <span>• Middlesex County</span>
              <span>• Sussex County</span>
              <span>• Somerset County</span>
              <span>• Union County</span>
            </div>
            <div className="mt-6 p-4 bg-white/5 rounded-xl border border-white/10">
              <p className="text-xs text-white/50">
                <span className="text-[#CFB973] font-semibold">License:</span> #13VH13590600
              </p>
              <p className="text-xs text-white/50 mt-1">Fully Licensed & Insured</p>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <p className="text-[#CFB973] italic text-xl font-medium mb-6">
            "We're not an insurance policy. We're your home's reassurance plan."
          </p>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-center md:text-left">
            <p className="text-white/50 text-sm">
              &copy; {new Date().getFullYear()} Valenrock™ Remodelers, LLC d/b/a Valenrock™ Home Services. All rights reserved.
            </p>
            <p className="text-white/40 text-xs mt-1">
              Specialized trade work performed by licensed, insured subcontractors in our vetted network.
            </p>
          </div>
          <div className="flex items-center gap-6 text-sm">
            <Link to="/privacy-policy" className="text-white/50 hover:text-[#CFB973] transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="text-white/50 hover:text-[#CFB973] transition-colors">
              Terms of Service
            </Link>
            <Link to="/disclaimer" className="text-white/50 hover:text-[#CFB973] transition-colors">
              Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
